import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Typography, Paper, Grid, Button, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, Select, MenuItem, FormControl, InputLabel,
  TablePagination, Tooltip, IconButton, ButtonGroup
} from '@material-ui/core'
import { Refresh as RefreshIcon, Visibility as ViewIcon } from '@material-ui/icons'
import ParseLogDetailDialog from './ParseLogDetailDialog'
import ParseLogStats from './ParseLogStats'

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'parsed', label: 'Parsed' },
  { value: 'parsed_with_warnings', label: 'Parsed with warnings' },
  { value: 'empty_result', label: 'Empty result' },
  { value: 'parse_error', label: 'Parse error' },
  { value: 'exception', label: 'Exception' },
  { value: 'unsupported_format', label: 'Unsupported format' }
]

const EXTENSION_OPTIONS = [
  { value: '', label: 'All extensions' },
  { value: 'pdf', label: 'PDF' },
  { value: 'xlsx', label: 'XLSX' },
  { value: 'xls', label: 'XLS' },
  { value: 'csv', label: 'CSV' }
]

const SUCCESS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Successful' },
  { value: 'false', label: 'Failed' }
]

const OUTCOME_CHIP = {
  parsed: { label: 'parsed', style: { backgroundColor: '#c8e6c9', color: '#1b5e20' } },
  parsed_with_warnings: { label: 'warnings', style: { backgroundColor: '#ffe0b2', color: '#e65100' } },
  empty_result: { label: 'empty', style: { backgroundColor: '#eeeeee', color: '#424242' } },
  parse_error: { label: 'parse_error', style: { backgroundColor: '#ffcdd2', color: '#b71c1c' } },
  exception: { label: 'exception', style: { backgroundColor: '#ffcdd2', color: '#b71c1c' } },
  unsupported_format: { label: 'unsupported', style: { backgroundColor: '#eeeeee', color: '#424242' } }
}

const outcomeChipProps = (outcome) =>
  OUTCOME_CHIP[outcome] || { label: outcome || 'unknown', style: { backgroundColor: '#eeeeee', color: '#424242' } }

const ROW_BG = {
  parsed: 'rgba(76, 175, 80, 0.06)',
  parsed_with_warnings: 'rgba(255, 152, 0, 0.08)',
  parse_error: 'rgba(244, 67, 54, 0.08)',
  exception: 'rgba(244, 67, 54, 0.08)'
}

const relativeTime = (dateStr) => {
  if (!dateStr) return '—'
  const diff = Math.max(0, Date.now() - new Date(dateStr).getTime())
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

const TIME_RANGES = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  all: null
}

const ParseLogPage = (props) => {
  const [items, setItems] = React.useState([])
  const [total, setTotal] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [stats, setStats] = React.useState(null)
  const [statsLoading, setStatsLoading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(25)
  const [outcomeFilter, setOutcomeFilter] = React.useState('')
  const [extensionFilter, setExtensionFilter] = React.useState('')
  const [successFilter, setSuccessFilter] = React.useState('')
  const [searchInput, setSearchInput] = React.useState('')
  const [searchDebounced, setSearchDebounced] = React.useState('')
  const [timeRange, setTimeRange] = React.useState('7d')
  const [detailOpen, setDetailOpen] = React.useState(false)
  const [detailId, setDetailId] = React.useState(null)

  React.useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  React.useEffect(() => {
    fetchStats()
  }, [])

  React.useEffect(() => {
    fetchItems()
  }, [page, pageSize, outcomeFilter, extensionFilter, successFilter, searchDebounced, timeRange])

  const fetchStats = () => {
    setStatsLoading(true)
    axios.get(`${props.url}/api/bid-estimation/parse-log/stats`)
      .then(res => { setStats(res.data); setStatsLoading(false) })
      .catch(err => { console.error('Failed to load parse stats', err); setStatsLoading(false) })
  }

  const fetchItems = () => {
    setLoading(true)
    const params = { page: page + 1, pageSize }
    if (outcomeFilter) params.outcome = outcomeFilter
    if (extensionFilter) params.extension = extensionFilter
    if (successFilter) params.success = successFilter
    if (searchDebounced) params.search = searchDebounced
    if (timeRange && TIME_RANGES[timeRange]) {
      params.since = new Date(Date.now() - TIME_RANGES[timeRange]).toISOString()
    }
    axios.get(`${props.url}/api/bid-estimation/parse-log`, { params })
      .then(res => {
        setItems(res.data.items || [])
        setTotal(res.data.total || 0)
        setLoading(false)
      })
      .catch(err => { console.error('Failed to load parse log', err); setLoading(false) })
  }

  const handleRefresh = () => { fetchStats(); fetchItems() }
  const handleOpenDetail = (id) => { setDetailId(id); setDetailOpen(true) }
  const handleCloseDetail = () => { setDetailOpen(false); setDetailId(null) }
  const handleDetailUpdate = () => { fetchItems(); fetchStats() }
  const handleFilterChange = (setter) => (e) => { setter(e.target.value); setPage(0) }

  const renderFilters = () => (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Outcome</InputLabel>
            <Select label="Outcome" value={outcomeFilter} onChange={handleFilterChange(setOutcomeFilter)}>
              {OUTCOME_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Extension</InputLabel>
            <Select label="Extension" value={extensionFilter} onChange={handleFilterChange(setExtensionFilter)}>
              {EXTENSION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Success</InputLabel>
            <Select label="Success" value={successFilter} onChange={handleFilterChange(setSuccessFilter)}>
              {SUCCESS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Search filename or error"
            size="small"
            variant="outlined"
            fullWidth
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(0) }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <ButtonGroup size="small" fullWidth>
            {['7d', '30d', 'all'].map(r => (
              <Button
                key={r}
                variant={timeRange === r ? 'contained' : 'outlined'}
                color={timeRange === r ? 'primary' : 'default'}
                onClick={() => { setTimeRange(r); setPage(0) }}
              >
                {r === 'all' ? 'All' : r === '7d' ? '7 days' : '30 days'}
              </Button>
            ))}
          </ButtonGroup>
        </Grid>
      </Grid>
    </Paper>
  )

  const renderRow = (row) => (
    <TableRow
      key={row._id}
      hover
      style={{ cursor: 'pointer', backgroundColor: ROW_BG[row.outcome] || 'transparent' }}
      onClick={() => handleOpenDetail(row._id)}
    >
      <TableCell>
        <Tooltip title={row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}>
          <span>{relativeTime(row.createdAt)}</span>
        </Tooltip>
      </TableCell>
      <TableCell style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip title={(row.file && row.file.originalFilename) || ''}>
          <span>{(row.file && row.file.originalFilename) || '—'}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {row.file && row.file.extension ? <Chip size="small" label={row.file.extension} /> : '—'}
      </TableCell>
      <TableCell style={{ fontFamily: 'Menlo, Monaco, "Courier New", monospace', fontSize: 11 }}>
        {(row.parser && row.parser.name) || '—'}
      </TableCell>
      <TableCell><Chip size="small" {...outcomeChipProps(row.outcome)} /></TableCell>
      <TableCell align="right">{row.itemsFound != null ? row.itemsFound : '—'}</TableCell>
      <TableCell align="right">{row.elapsedMs != null ? `${row.elapsedMs}ms` : '—'}</TableCell>
      <TableCell>
        {(row.user && (row.user.name || row.user.username)) || '—'}
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDetail(row._id) }}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  )

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography variant="h4">Parse Log</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading || statsLoading}
        >
          Refresh
        </Button>
      </div>

      <ParseLogStats stats={stats} loading={statsLoading} />
      {renderFilters()}

      <Paper>
        {loading && (
          <div style={{ textAlign: 'center', padding: 32 }}><CircularProgress /></div>
        )}
        {!loading && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell>Ext</TableCell>
                  <TableCell>Parser</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Elapsed</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" style={{ padding: 24, color: '#999' }}>
                      No parse attempts match the current filters.
                    </TableCell>
                  </TableRow>
                )}
                {items.map(renderRow)}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={(e, newPage) => setPage(newPage)}
          onChangeRowsPerPage={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
        />
      </Paper>

      <ParseLogDetailDialog
        open={detailOpen}
        onClose={handleCloseDetail}
        attemptId={detailId}
        url={props.url}
        onUpdate={handleDetailUpdate}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ParseLogPage)
